import React, { useEffect, useCallback, useMemo, useState } from "react";
import dayjs, { Dayjs } from "dayjs";
import { useForm, SubmitHandler } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { TerminalCall } from "../../types/TerminalCall";
import {
  hideChildModal,
  hideModal,
  showChildModal,
} from "../../data/modal";
import { runModalCallback } from "../../data/modalCallbacks";

import {
  Alert,
  Box,
  Button,
  Grid,
  InputAdornment,
  Paper,
  TextField,
  Typography,
} from "@mui/material";

import {
  AccessTime as AccessTimeIcon,
  CalendarMonth as CalendarMonthIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  EventAvailable as EventAvailableIcon,
  Save as SaveIcon,
} from "@mui/icons-material";

import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { deDE, enUS, frFR, nlNL } from "@mui/x-date-pickers/locales";

import "dayjs/locale/de";
import "dayjs/locale/fr";
import "dayjs/locale/nl";

import { selectedFilteredVoyage } from "../../selectors/filterSelector";
import { createSelectedTerminalCall } from "../../data/selectedterminalcalls";
import { useRefreshVoyageData } from "../hooks/useRefreshVoyageData";
import { getVessels } from "../../data/vessels";
import GetVesselOptions from "../FormHelpers/VesselSelectOptions";
import { SelectField } from "../FormFields/RenderSelectField";
import { getTerminals } from "../../data/terminals";
import { AutocompleteField } from "../FormFields/RenderAutocompleteField";
import { useTranslation } from "react-i18next";

interface SimpleTerminalCallFormProps {
  vessel?: any;
  terminal?: any;
  showVesselField?: boolean;
  showTerminalField?: boolean;
  callbackId?: string;
  callback?: (createdCall?: any) => void | Promise<void>;

  planned?: string | null;
  defaultPlanned?: string | null;
  initialPlanned?: string | null;
  terminalCallDefaults?: any;
  initialTerminalCall?: any;
}

type SimpleTerminalCallFormValues = TerminalCall & {
  planned?: string | null;
  dockingSide?: string | null;
};

const DOCKING_SIDE_PORT = "port";

const roundToNextHalfHour = () => {
  const now = dayjs().second(0).millisecond(0);
  const minutes = now.minute();
  const remainder = minutes % 30;

  if (remainder === 0) {
    return now;
  }

  return now.add(30 - remainder, "minute");
};

const getInitialPlannedDateTime = ({
  planned,
  defaultPlanned,
  initialPlanned,
  terminalCallDefaults,
  initialTerminalCall,
}: {
  planned?: string | null;
  defaultPlanned?: string | null;
  initialPlanned?: string | null;
  terminalCallDefaults?: any;
  initialTerminalCall?: any;
}) => {
  const plannedValue =
    planned ??
    defaultPlanned ??
    initialPlanned ??
    terminalCallDefaults?.planned ??
    initialTerminalCall?.planned ??
    null;

  if (plannedValue) {
    const parsedPlanned = dayjs(plannedValue);

    if (parsedPlanned.isValid()) {
      return parsedPlanned.second(0).millisecond(0);
    }
  }

  return roundToNextHalfHour();
};

const generateTimeOptions = () => {
  const options: string[] = [];

  for (let hour = 0; hour < 24; hour += 1) {
    options.push(`${String(hour).padStart(2, "0")}:00`);
    options.push(`${String(hour).padStart(2, "0")}:30`);
  }

  return options;
};

const normalizeCollection = (value: any): any[] => {
  if (Array.isArray(value)) {
    return value;
  }

  if (Array.isArray(value?.data)) {
    return value.data;
  }

  if (Array.isArray(value?.["hydra:member"])) {
    return value["hydra:member"];
  }

  if (Array.isArray(value?.member)) {
    return value.member;
  }

  if (Array.isArray(value?.items)) {
    return value.items;
  }

  return [];
};

const getNormalizedLanguage = (language?: string) => {
  const normalizedLanguage = (language ?? "en").split("-")[0].toLowerCase();

  if (["fr", "nl", "de", "en"].includes(normalizedLanguage)) {
    return normalizedLanguage;
  }

  return "en";
};

const getPickerLocaleText = (language: string) => {
  switch (language) {
    case "fr":
      return frFR.components.MuiLocalizationProvider.defaultProps.localeText;

    case "nl":
      return nlNL.components.MuiLocalizationProvider.defaultProps.localeText;

    case "de":
      return deDE.components.MuiLocalizationProvider.defaultProps.localeText;

    case "en":
    default:
      return enUS.components.MuiLocalizationProvider.defaultProps.localeText;
  }
};

const getVesselApiId = (value: any) => {
  if (!value) {
    return null;
  }

  if (typeof value === "string") {
    return value;
  }

  if (value["@id"]) {
    return value["@id"];
  }

  if (value.id) {
    return `/api/vessels/${value.id}`;
  }

  return null;
};

const getTerminalApiId = (value: any) => {
  if (!value) {
    return null;
  }

  if (typeof value === "string") {
    return value;
  }

  if (value["@id"]) {
    return value["@id"];
  }

  if (value.id) {
    return `/api/terminals/${value.id}`;
  }

  return null;
};

const findVesselByValue = (vessels: any[], value: any) => {
  const selectedVesselId = getVesselApiId(value);

  if (!selectedVesselId) {
    return null;
  }

  const numericId = String(selectedVesselId).split("/").pop();

  return (
    vessels.find((vesselItem: any) => {
      return (
        vesselItem?.["@id"] === selectedVesselId ||
        String(vesselItem?.id) === String(numericId)
      );
    }) ?? null
  );
};

const getPreferredDockingSideFromVessel = (selectedVessel: any) => {
  return selectedVessel?.preferredDockingSide || DOCKING_SIDE_PORT;
};

const SimpleTerminalCallForm: React.FC<SimpleTerminalCallFormProps> = ({
  vessel,
  terminal,
  showVesselField = false,
  showTerminalField = false,
  callbackId,
  callback,

  planned,
  defaultPlanned,
  initialPlanned,
  terminalCallDefaults,
  initialTerminalCall,
}) => {
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  const { voyage } = useSelector(selectedFilteredVoyage);
  const refreshVoyageData = useRefreshVoyageData();

  const vesselsState = useSelector((state: any) => state.vessels?.data);
  const terminalsState = useSelector((state: any) => state.terminals?.data);
  const modal = useSelector((state: any) => state.modal);

  const vessels = useMemo(() => normalizeCollection(vesselsState), [vesselsState]);
  const terminals = useMemo(
    () => normalizeCollection(terminalsState),
    [terminalsState]
  );

  const language = useMemo(
    () => getNormalizedLanguage(i18n.resolvedLanguage ?? i18n.language),
    [i18n.resolvedLanguage, i18n.language]
  );

  const pickerLocaleText = useMemo(
    () => getPickerLocaleText(language),
    [language]
  );

  useEffect(() => {
    dayjs.locale(language);
  }, [language]);

  const openChildModal = useCallback(
    (opts: any) => dispatch(showChildModal(opts) as any),
    [dispatch]
  );

  const timeOptions = useMemo(() => generateTimeOptions(), []);

  const initialPlannedDateTime = useMemo(() => {
    return getInitialPlannedDateTime({
      planned,
      defaultPlanned,
      initialPlanned,
      terminalCallDefaults,
      initialTerminalCall,
    });
  }, [
    planned,
    defaultPlanned,
    initialPlanned,
    terminalCallDefaults,
    initialTerminalCall,
  ]);

  const [selectedDateTime, setSelectedDateTime] = useState<Dayjs>(
    initialPlannedDateTime
  );

  const {
    handleSubmit,
    control,
    formState,
    setValue,
    watch,
  } = useForm<SimpleTerminalCallFormValues>({
    defaultValues: {
      ...new TerminalCall(),
      ...(terminalCallDefaults || {}),
      ...(initialTerminalCall || {}),
      voyage: voyage?.["@id"] ?? null,
      vessel: getVesselApiId(vessel),
      terminal: getTerminalApiId(terminal),
      planned: initialPlannedDateTime.toISOString(),
      dockingSide: getPreferredDockingSideFromVessel(vessel),
    },
  });

  const { isSubmitting } = formState;
  const selectedTerminal = watch("terminal");
  const selectedVesselValue = watch("vessel");

  const selectedVesselForForm = useMemo(() => {
    if (!showVesselField && vessel) {
      return vessel;
    }

    return findVesselByValue(vessels, selectedVesselValue) ?? vessel;
  }, [showVesselField, vessel, vessels, selectedVesselValue]);

  const refreshOptions = useCallback(async () => {
    await Promise.all([
      dispatch(getVessels() as any),
      dispatch(getTerminals() as any),
    ]);
  }, [dispatch]);

  useEffect(() => {
    refreshOptions();
  }, [refreshOptions]);

  useEffect(() => {
    setSelectedDateTime(initialPlannedDateTime);

    setValue("planned", initialPlannedDateTime.toISOString(), {
      shouldDirty: false,
      shouldValidate: true,
    });
  }, [initialPlannedDateTime, setValue]);

  useEffect(() => {
    if (voyage?.["@id"]) {
      setValue("voyage", voyage["@id"], {
        shouldDirty: false,
        shouldValidate: true,
      });
    }
  }, [voyage, setValue]);

  useEffect(() => {
    const vesselId = getVesselApiId(vessel);

    if (vesselId) {
      setValue("vessel", vesselId, {
        shouldDirty: false,
        shouldValidate: true,
      });
    }
  }, [vessel, setValue]);

  useEffect(() => {
    const terminalId = getTerminalApiId(terminal);

    if (terminalId) {
      setValue("terminal", terminalId, {
        shouldDirty: false,
        shouldValidate: true,
      });
    }
  }, [terminal, setValue]);

  useEffect(() => {
    setValue("planned", selectedDateTime.toISOString(), {
      shouldDirty: true,
      shouldValidate: true,
    });
  }, [selectedDateTime, setValue]);

  useEffect(() => {
    const preferredDockingSide =
      getPreferredDockingSideFromVessel(selectedVesselForForm);

    setValue("dockingSide", preferredDockingSide, {
      shouldDirty: true,
      shouldValidate: true,
    });
  }, [selectedVesselForForm, setValue]);

  const close = useCallback(() => {
    if (modal.childModal) {
      dispatch(hideChildModal() as any);
      return;
    }

    dispatch(hideModal() as any);
  }, [dispatch, modal.childModal]);

  const handleTerminalSaved = useCallback(
    async (savedTerminal: any) => {
      const savedTerminalId = getTerminalApiId(savedTerminal);

      await refreshOptions();

      if (savedTerminalId) {
        setValue("terminal", savedTerminalId, {
          shouldDirty: true,
          shouldValidate: true,
        });
      }
    },
    [refreshOptions, setValue]
  );

  const handleDateChange = (date: Dayjs | null) => {
    if (!date) {
      return;
    }

    setSelectedDateTime((current) =>
      current.year(date.year()).month(date.month()).date(date.date())
    );
  };

  const handleTimeChange = (time: string) => {
    const [hour, minute] = time.split(":").map(Number);

    setSelectedDateTime((current) =>
      current.hour(hour).minute(minute).second(0).millisecond(0)
    );
  };

  const selectedTime = selectedDateTime.format("HH:mm");

  const formattedSelectedDate = useMemo(() => {
    return selectedDateTime.locale(language).format("D MMMM YYYY");
  }, [selectedDateTime, language]);

  const formattedDateTimeFieldValue = useMemo(() => {
    return selectedDateTime.locale(language).format("DD/MM/YYYY HH:mm");
  }, [selectedDateTime, language]);

  const onSubmit: SubmitHandler<SimpleTerminalCallFormValues> = async (data) => {
    try {
      const preferredDockingSide =
        getPreferredDockingSideFromVessel(selectedVesselForForm);

      const payload = {
        ...data,
        voyage: voyage?.["@id"] ?? data.voyage,
        planned: selectedDateTime.toISOString(),
        dockingSide: preferredDockingSide,
      };

      const createdCall = await dispatch(createSelectedTerminalCall(payload) as any);

      if (callbackId) {
        await runModalCallback(callbackId, createdCall || payload);
      }

      if (typeof callback === "function") {
        await callback(createdCall || payload);
      }

      await refreshVoyageData(voyage);
      close();
    } catch (error) {
      console.error(t("failedToCreatePlanning"), error);
    }
  };

  return (
    <LocalizationProvider
      dateAdapter={AdapterDayjs}
      adapterLocale={language}
      localeText={pickerLocaleText}
    >
      <Box
        component="form"
        onSubmit={handleSubmit(onSubmit)}
        sx={{
          width: "100%",
          bgcolor: "background.paper",
        }}
      >
        <Grid container flexDirection="column" gap={3}>
          <Grid item>
            <Grid container spacing={2}>
              {showVesselField ? (
                <Grid item xs={12}>
                  <Typography fontWeight={700} mb={1}>
                    {t("requiredField", { field: t("vessel") })}
                  </Typography>

                  <SelectField
                    name="vessel"
                    label={t("vessel")}
                    control={control}
                    options={GetVesselOptions(vessels, false)}
                    required
                  />
                </Grid>
              ) : null}

              {showTerminalField ? (
                <Grid item xs={12}>
                  <Typography fontWeight={700} mb={1}>
                    {t("requiredField", { field: t("terminal") })}
                  </Typography>

                  <AutocompleteField
                    name="terminal"
                    label={t("searchForTerminal")}
                    control={control}
                    labelIdentifier={["name"]}
                    options={terminals}
                    required
                    onCreateClick={() =>
                      openChildModal({
                        title: t("addTerminal"),
                        description: t("createTerminalDescription"),
                        form: "terminal",
                        formProps: {
                          mode: "create",
                          callback: handleTerminalSaved,
                        },
                      })
                    }
                    onEditClick={(terminalData: any) =>
                      openChildModal({
                        title: t("editTerminal"),
                        description: t("modifyTerminalDescription"),
                        form: "terminal",
                        formProps: {
                          terminal: terminalData,
                          mode: "edit",
                          callback: handleTerminalSaved,
                        },
                      })
                    }
                    onInfoClick={(terminalData: any) =>
                      openChildModal({
                        title: terminalData.name,
                        description: t("terminalMap"),
                        form: "terminalmapmodal",
                        formProps: {
                          terminal: terminalData,
                          callbackId,
                        },
                      })
                    }
                  />

                  {!selectedTerminal ? (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: "block", mt: 0.75, ml: 0 }}
                    >
                      {t("startTypingToFindTerminal")}
                    </Typography>
                  ) : null}
                </Grid>
              ) : null}

              <Grid item xs={12}>
                <Typography fontWeight={700} mb={1}>
                  {t("requiredField", { field: t("plannedDateTime") })}
                </Typography>

                <TextField
                  fullWidth
                  value={formattedDateTimeFieldValue}
                  InputProps={{
                    readOnly: true,
                    startAdornment: (
                      <InputAdornment position="start">
                        <CalendarMonthIcon color="action" />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <AccessTimeIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </Grid>
          </Grid>

          <Grid item>
            <Paper
              variant="outlined"
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  md: "1fr 250px",
                },
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  minWidth: 0,
                  borderRight: {
                    xs: "none",
                    md: "1px solid",
                  },
                  borderColor: "divider",
                }}
              >
                <DateCalendar
                  value={selectedDateTime}
                  onChange={handleDateChange}
                  sx={{
                    width: "100%",
                    maxWidth: "100%",
                    "& .MuiPickersCalendarHeader-root": {
                      px: 2,
                    },
                    "& .MuiDayCalendar-header": {
                      justifyContent: "space-around",
                    },
                    "& .MuiDayCalendar-weekContainer": {
                      justifyContent: "space-around",
                    },
                  }}
                />
              </Box>

              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  minHeight: {
                    xs: 260,
                    md: 420,
                  },
                  maxHeight: {
                    xs: 260,
                    md: 420,
                  },
                }}
              >
                <Box
                  sx={{
                    px: 2,
                    py: 1.5,
                    borderBottom: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  <Typography fontWeight={700}>{t("time")}</Typography>
                </Box>

                <Box
                  sx={{
                    overflowY: "auto",
                    flex: 1,
                  }}
                >
                  {timeOptions.map((time) => {
                    const active = selectedTime === time;

                    return (
                      <Button
                        key={time}
                        fullWidth
                        onClick={() => handleTimeChange(time)}
                        sx={{
                          justifyContent: "space-between",
                          borderRadius: 0,
                          px: 2,
                          py: 1.35,
                          color: active ? "primary.main" : "text.primary",
                          bgcolor: active ? "primary.50" : "background.paper",
                          borderBottom: "1px solid",
                          borderColor: "divider",
                          textTransform: "none",
                          fontWeight: active ? 700 : 400,
                          "&:hover": {
                            bgcolor: active ? "primary.100" : "action.hover",
                          },
                        }}
                      >
                        <span>{time}</span>
                        {active ? <CheckIcon fontSize="small" /> : null}
                      </Button>
                    );
                  })}
                </Box>
              </Box>
            </Paper>
          </Grid>

          <Grid item>
            <Alert
              icon={
                <EventAvailableIcon
                  sx={{
                    fontSize: 36,
                  }}
                />
              }
              severity="info"
              variant="outlined"
              sx={{
                alignItems: "center",
                "& .MuiAlert-message": {
                  width: "100%",
                },
              }}
            >
              <Typography variant="caption" color="text.secondary">
                {t("selected")}
              </Typography>

              <Typography fontWeight={700}>
                {formattedSelectedDate} {t("at")}{" "}
                {selectedDateTime.format("HH:mm")}
              </Typography>

              <Typography variant="body2" color="text.secondary">
                {t("localTime")}
              </Typography>
            </Alert>
          </Grid>

          <Grid
            item
            className="button-wrapper"
            sx={{
              display: "flex",
              justifyContent: "space-between",
              gap: 2,
              position: "sticky",
              bottom: 0,
              bgcolor: "background.paper",
              borderTop: "1px solid",
              borderColor: "divider",
              pt: 2,
              pb: 0,
              zIndex: 2,
            }}
          >
            <Button
              onClick={close}
              disabled={isSubmitting}
              variant="outlined"
              startIcon={<CloseIcon />}
            >
              {t("cancel")}
            </Button>

            <Button
              type="submit"
              disabled={isSubmitting}
              variant="contained"
              startIcon={<SaveIcon />}
            >
              {t("createPlanning")}
            </Button>
          </Grid>
        </Grid>
      </Box>
    </LocalizationProvider>
  );
};

export default SimpleTerminalCallForm;